import { saveVerificationPhone, phoneSavePatch } from "@/features/phone-verification/phoneVerificationService.js";
// src/pages/onboarding/patient/PatientOnboarding.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { loadProgress, saveProgress } from "@/lib/persistStep";
import supabase from "@/services/supabaseClient";
import { loadPatientIntroPreview } from "@/lib/patientIntroPreview";
import { savePatientIntroPreview } from "@/lib/patientIntroPreview";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { splitE164ToCcLocal } from "@/utils/phone.js";
import { normalizeConditionList } from "@/utils/conditions";
import {
  ENABLE_ONBOARDING_PHONE_OTP,
  buildOnboardingIntroSteps,
  normalizeIntroStepForPhoneOtpFlag,
} from "@/pages/onboarding/onboardingPhoneOtpFeature.js";

// Intro (phone/otp/name/profile complete)
import StepPhone from "@/pages/onboarding/shared/steps/StepPhone.jsx";
import StepOtp from "@/pages/onboarding/shared/steps/StepOtp.jsx";
import StepName from "@/pages/onboarding/shared/steps/StepName.jsx";
import StepPasscodeCreate from "@/pages/onboarding/shared/steps/StepPasscodeCreate.jsx";
import StepPasscodeConfirm from "@/pages/onboarding/shared/steps/StepPasscodeConfirm.jsx";
import StepDob from "@/pages/onboarding/shared/steps/StepDob.jsx";
import StepDonePatient from "@/pages/onboarding/patient/steps/StepDonePatient.jsx";

// Profile
import StepIDPassport from "@/pages/onboarding/shared/steps/StepIDPassport.jsx";
import StepLanguages from "@/pages/onboarding/shared/steps/StepLanguages.jsx";
import StepBloodType from "@/pages/onboarding/patient/steps/StepBloodType.jsx";
import StepConditions from "@/pages/onboarding/patient/steps/StepConditions.jsx";
import StepAllergies from "@/pages/onboarding/patient/steps/StepAllergies.jsx";
import StepMedications from "@/pages/onboarding/patient/steps/StepMedications.jsx";
import StepProfileComplete from "@/pages/onboarding/patient/steps/StepProfileComplete.jsx";
import { markSettingsWelcomePending } from "@/lib/settingsWelcomeState.js";
import { writeOnboardingEvent, writeUserActivityEvent } from "@/services/userTruthTracking.js";
import { shouldOfferCreatePassword } from "@/features/security/accountSecurityState.js";

function hasStoredPasscode(profileRow) {
  return !!(
    profileRow?.passcode_hash ||
    profileRow?.passcode_set_at ||
    profileRow?.passcode_updated_at ||
    profileRow?.passcode
  );
}

function hasPasswordMarker(user) {
  const meta = user?.user_metadata || {};
  return meta.password_set === true || !!String(meta.password_changed_at || "").trim();
}

const LS_KEY = "onb_patient_v2";
const PATIENT_SELECTION_SESSION_KEY = "afrimed:onboarding:selected_patient_session";
const EMPTY_ID_VERIFICATION = {
  captured: null,
  uploadMeta: null,
  verifyStatus: "idle",
  verifyMsg: "",
  ocrStatus: "idle",
  ocrMsg: "",
  ocrSummary: null,
  stagingBypassEligible: false,
  stagingTesterEmail: "",
  stagingBypass: false,
  stagingDeferred: false,
};
const BASE_INTRO_STEPS = [
  "name",
  "phone",
  "otp",
  "profileComplete",
];
const INTRO_STEPS = buildOnboardingIntroSteps(BASE_INTRO_STEPS);

function shouldUseDeferredOnboardingProfileSteps() {
  return false;
}

function buildSeedState(ownerUserId = "") {
  return {
    ownerUserId: String(ownerUserId || "").trim(),
    phase: "intro",
    step: 0,
    profileStep: 0,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneCc: "+27",
    agreed: false,
    e164: null,
    passcodeDraft: "",
    passcodeConfirm: "",
    dateOfBirth: "",
    idDoc: "",
    idVerification: { ...EMPTY_ID_VERIFICATION },
    languages: [],
    preferredLanguage: "",
    bloodType: "",
    conditions: [],
    allergies: [],
    medications: [],
  };
}

function hasCurrentSessionPatientSelection(userId = "") {
  const uid = String(userId || "").trim();
  if (!uid) return false;
  try {
    return String(sessionStorage.getItem(PATIENT_SELECTION_SESSION_KEY) || "").trim() === uid;
  } catch {
    return false;
  }
}

async function hasTrustedPharmacyInviteConflict(user) {
  const userId = String(user?.id || "").trim();
  const email = String(user?.email || user?.user_metadata?.email || "").trim().toLowerCase();
  if (!userId || !email || hasCurrentSessionPatientSelection(userId)) return false;

  const [roleResult, inviteResult] = await Promise.all([
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "pharmacy")
      .limit(1),
    supabase
      .from("staging_access_invites")
      .select("role_hint,allowed")
      .eq("email", email)
      .eq("allowed", true)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const hasPharmacyUserRole =
    !roleResult.error &&
    Array.isArray(roleResult.data) &&
    roleResult.data.some((row) => String(row?.role || "").trim().toLowerCase() === "pharmacy");
  const inviteRole = String(inviteResult.data?.role_hint || "").trim().toLowerCase();
  const hasPharmacyInvite = !inviteResult.error && (inviteRole === "pharmacy" || inviteRole === "pharmacist");
  return hasPharmacyUserRole && hasPharmacyInvite;
}

function normalizeStringList(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function orderLanguagesWithPreferred(items, preferred) {
  const preferredClean = String(preferred || "").trim();
  const deduped = Array.from(
    new Map(
      [
        ...(preferredClean ? [preferredClean] : []),
        ...normalizeStringList(items),
      ]
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .map((item) => [item.toLowerCase(), item])
    ).values()
  );

  if (!preferredClean) return deduped;

  return [
    preferredClean,
    ...deduped.filter((item) => item.toLowerCase() !== preferredClean.toLowerCase()),
  ];
}

function textValue(value) {
  return String(value || "").trim();
}

function buildE164FromParts(countryCode, localNumber) {
  const cc = textValue(countryCode || "+27");
  const digits = textValue(localNumber).replace(/\D/g, "");
  if (!digits) return "";
  return `${cc.startsWith("+") ? cc : `+${cc}`}${digits.replace(/^0+/, "")}`;
}

function normalizeIdVerificationState(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    captured:
      src.captured && typeof src.captured === "object"
        ? {
            name: String(src.captured.name || "").trim(),
            size: Number(src.captured.size || 0) || 0,
            type: String(src.captured.type || "").trim(),
          }
        : null,
    uploadMeta: src.uploadMeta && typeof src.uploadMeta === "object" ? src.uploadMeta : null,
    verifyStatus: String(src.verifyStatus || "idle").trim() || "idle",
    verifyMsg: String(src.verifyMsg || "").trim(),
    ocrStatus: String(src.ocrStatus || "idle").trim() || "idle",
    ocrMsg: String(src.ocrMsg || "").trim(),
    ocrSummary: src.ocrSummary && typeof src.ocrSummary === "object" ? src.ocrSummary : null,
    stagingBypassEligible: src.stagingBypassEligible === true,
    stagingTesterEmail: String(src.stagingTesterEmail || "").trim().toLowerCase(),
    stagingBypass: src.stagingBypass === true,
    stagingDeferred: src.stagingDeferred === true,
  };
}

function sanitizeRestoredState(raw, ownerUserId = "") {
  const restoredRaw = raw && typeof raw === "object" ? raw : {};
  const clamp = (n, lo, hi) =>
    Number.isFinite(n) ? Math.min(Math.max(n, lo), hi) : lo;

  const PROFILE_MAX = 6;
  const safePhase =
    restoredRaw.phase === "intro" || restoredRaw.phase === "profile"
      ? restoredRaw.phase
      : "intro";

  return {
    ownerUserId: String(ownerUserId || restoredRaw.ownerUserId || "").trim(),
    phase: safePhase,
    step: normalizeIntroStepForPhoneOtpFlag(restoredRaw.step, BASE_INTRO_STEPS, INTRO_STEPS),
    profileStep: clamp(restoredRaw.profileStep, 0, PROFILE_MAX),
    firstName: restoredRaw.firstName ?? "",
    lastName: restoredRaw.lastName ?? "",
    email: restoredRaw.email ?? "",
    phone: restoredRaw.phone ?? "",
    phoneCc: restoredRaw.phoneCc ?? "+27",
    agreed: !!restoredRaw.agreed,
    e164: restoredRaw.e164 ?? null,
    passcodeDraft: restoredRaw.passcodeDraft ?? "",
    passcodeConfirm: restoredRaw.passcodeConfirm ?? "",
    dateOfBirth: restoredRaw.dateOfBirth ?? "",
    idDoc: restoredRaw.idDoc ?? "",
    idVerification: normalizeIdVerificationState(restoredRaw.idVerification),
    languages: normalizeStringList(restoredRaw.languages),
    preferredLanguage: String(restoredRaw.preferredLanguage || restoredRaw.primaryLanguage || "").trim(),
    bloodType: String(restoredRaw.bloodType || "").trim(),
    conditions: normalizeConditionList(restoredRaw.conditions),
    allergies: normalizeStringList(restoredRaw.allergies),
    medications: normalizeStringList(restoredRaw.medications),
  };
}

function mapPatientOnboardingStepKey({ phase, introStep, profileStep }) {
  if (phase === "intro") {
    switch (introStep) {
      case "name":
        return "step_name";
      case "phone":
        return "step_phone";
      case "otp":
        return "step_otp";
      case "profileComplete":
        return "profile_complete";
      case "passcodeCreate":
        return "step_passcode_create";
      case "passcodeConfirm":
        return "step_passcode_confirm";
      case "dob":
        return "step_dob";
      case "done":
        return "step_done";
      default:
        return "";
    }
  }

  switch (profileStep) {
    case 0:
      return "profile_id";
    case 1:
      return "profile_languages";
    case 2:
      return "profile_blood_type";
    case 3:
      return "profile_conditions";
    case 4:
      return "profile_allergies";
    case 5:
      return "profile_medications";
    case 6:
      return "profile_complete";
    default:
      return "";
  }
}

function resolvePatientScreenLabel({ phase, introStep, profileStep }) {
  if (phase === "intro") {
    switch (introStep) {
      case "name":
        return "StepName";
      case "phone":
        return "StepPhone";
      case "otp":
        return "StepOtp";
      case "profileComplete":
        return "StepProfileComplete";
      case "passcodeCreate":
        return "StepPasscodeCreate";
      case "passcodeConfirm":
        return "StepPasscodeConfirm";
      case "dob":
        return "StepDob";
      case "done":
        return "StepDonePatient";
      default:
        return "UnknownPatientOnboardingStep";
    }
  }

  if (!shouldUseDeferredOnboardingProfileSteps()) return "StepProfileComplete";

  switch (profileStep) {
    case 0:
      return "StepIDPassport";
    case 1:
      return "StepLanguages";
    case 2:
      return "StepBloodType";
    case 3:
      return "StepConditions";
    case 4:
      return "StepAllergies";
    case 5:
      return "StepMedications";
    case 6:
      return "StepProfileComplete";
    default:
      return "UnknownPatientProfileStep";
  }
}

function collectPatientResumeFacts({ user, dbProfile, profile, introSteps }) {
  const fullName = String(dbProfile?.full_name || profile?.display_name || "").trim();
  const phone = String(dbProfile?.phone || dbProfile?.phone_number || user?.phone || "").trim();
  const hasDob = !!String(dbProfile?.date_of_birth || "").trim();
  const hasPasscode = hasStoredPasscode(dbProfile);
  const passwordMarker = hasPasswordMarker(user);
  const passwordRequired =
    String(import.meta.env.VITE_AUTH_PASSWORD_PRIMARY_ENABLED || "")
      .trim()
      .toLowerCase() === "true";
  return {
    onboarding_completed: profile?.onboarding_completed === true,
    has_name: !!fullName,
    has_phone: !!phone,
    has_password_marker: passwordMarker,
    password_required: passwordRequired,
    has_passcode_marker: hasPasscode,
    has_dob: hasDob,
    otp_enabled: ENABLE_ONBOARDING_PHONE_OTP,
    intro_steps: Array.isArray(introSteps) ? introSteps : [],
  };
}

function resolvePatientResumeTarget({
  facts,
  introSteps,
  previousPhase,
  previousStep,
  previousProfileStep,
  preserveFirstUseOrder = false,
}) {
  const activeSteps = Array.isArray(introSteps) && introSteps.length ? introSteps : INTRO_STEPS;
  const stepIndex = (stepName) => {
    const idx = activeSteps.indexOf(stepName);
    return idx === -1 ? 0 : idx;
  };
  const skipSource = [];

  let target = {
    phase: previousPhase === "profile" ? "profile" : "intro",
    step: Number.isFinite(Number(previousStep))
      ? Math.min(Math.max(Number(previousStep), 0), Math.max(activeSteps.length - 1, 0))
      : 0,
    profileStep: Number.isFinite(Number(previousProfileStep))
      ? Math.min(Math.max(Number(previousProfileStep), 0), 6)
      : 0,
    requiresCreatePassword: false,
  };

  // A server-resolved first-use handoff deliberately presents the Patient
  // sequence in order. Auth/profile metadata can pre-populate fields, but it
  // must not silently turn those inferred values into completed steps.
  if (preserveFirstUseOrder) {
    skipSource.push("controlled_first_use_order");
  } else {
    if (facts.has_name) {
      skipSource.push("db_full_name_present");
      target.phase = "intro";
      target.step = Math.max(target.step, stepIndex("phone"));
    }

    if (facts.has_phone) {
      skipSource.push("db_phone_present");
      target.phase = "intro";
      target.step = Math.max(target.step, stepIndex(facts.otp_enabled ? "otp" : "profileComplete"));
      if (!facts.otp_enabled) skipSource.push("otp_disabled");
    }

    if (facts.has_passcode_marker) {
      skipSource.push("passcode_present");
      target.phase = "intro";
      target.step = Math.max(target.step, stepIndex("profileComplete"));
    }

    if (facts.has_dob) {
      skipSource.push("db_dob_present");
      target.phase = "intro";
      target.step = Math.max(target.step, stepIndex("profileComplete"));
    }

    if (facts.password_required && facts.has_name && facts.has_phone && !facts.has_password_marker) {
      skipSource.push("password_missing");
      target.requiresCreatePassword = true;
    }
  }

  const introStep = activeSteps[target.step] || activeSteps[0] || "name";
  if (introStep === "profileComplete") skipSource.push("profile_complete_ready");

  return {
    ...target,
    introStep,
    resolvedScreenLabel: target.requiresCreatePassword
      ? "CreatePassword"
      : resolvePatientScreenLabel({
          phase: target.phase,
          introStep,
          profileStep: target.profileStep,
        }),
    skipSource,
  };
}

export default function PatientOnboarding({ backToCategory, preserveFirstUseOrder = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const forceFresh = sp.get("fresh") === "1";

  const { user, profile, loading } = useAuth();

  async function commitPatientNameToDb() {
  // Never block onboarding UX on this. Hard timeout + swallow errors.
  const withTimeout = (p, ms) =>
    Promise.race([
      p,
      new Promise((_, rej) =>
        setTimeout(() => rej(new Error("commit timeout")), ms)
      ),
    ]);

  try {
    const { data, error: authErr } = await withTimeout(
      supabase.auth.getUser(),
      4000
    );

    const user = data?.user;
    if (authErr || !user?.id) return;

    const preview = loadPatientIntroPreview(user.id) || {};
    const full =
      String(preview?.name || preview?.full_name || "").trim() ||
      `${String(preview?.firstName || "").trim()} ${String(
        preview?.lastName || ""
      ).trim()}`.trim();

    if (!full) return;

    // DB upsert (timeout, ignore failures)
    await withTimeout(
      supabase
        .from("patient_profiles")
        .upsert({ user_id: user.id, full_name: full }, { onConflict: "user_id" }),
      4000
    ).catch(() => {});

    // Auth metadata (timeout, ignore failures)
    await withTimeout(
      supabase.auth.updateUser({ data: { full_name: full } }),
      4000
    ).catch(() => {});
  } catch {
    // silent by design
  }
}

  // ---- Restore only after the stored snapshot proves it belongs to the current user ----
const restoredRaw = loadProgress(LS_KEY) ?? {};
const renderUserId = String(user?.id || "").trim();
const restoredOwnerUserId = String(restoredRaw?.ownerUserId || "").trim();
const restored =
  renderUserId && restoredOwnerUserId === renderUserId
    ? sanitizeRestoredState(restoredRaw, renderUserId)
    : buildSeedState(renderUserId);

  // ---- State ----
  const [phase, setPhase] = useState(restored.phase);
  const [step, setStep] = useState(restored.step);
  const [profileStep, setProfileStep] = useState(restored.profileStep);
  const [firstName, setFirstName] = useState(restored.firstName);
  const [lastName, setLastName] = useState(restored.lastName);

  const [languages, setLanguages] = useState(restored.languages);
  const [preferredLanguage, setPreferredLanguage] = useState(restored.preferredLanguage);
  const [email, setEmail] = useState(restored.email);
  const [phone, setPhone] = useState(restored.phone);
  const [phoneCc, setPhoneCc] = useState(restored.phoneCc);
  const [agreed, setAgreed] = useState(restored.agreed);
  const [e164, setE164] = useState(restored.e164);
  const [passcodeDraft, setPasscodeDraft] = useState(restored.passcodeDraft);
  const [passcodeConfirm, setPasscodeConfirm] = useState(restored.passcodeConfirm);
  const [dateOfBirth, setDateOfBirth] = useState(restored.dateOfBirth);

  const [idDoc, setIdDoc] = useState(restored.idDoc);
  const [idVerification, setIdVerification] = useState(restored.idVerification);
  const [bloodType, setBloodType] = useState(restored.bloodType);
  const [conditions, setConditions] = useState(restored.conditions);
  const [allergies, setAllergies] = useState(restored.allergies);
  const [medications, setMedications] = useState(restored.medications);
  const scopedRestoreUserRef = useRef("");
  const renderSourceRef = useRef("initial");
  const resumeDecisionRef = useRef(null);
  const [resumeFacts, setResumeFacts] = useState(null);

  const applyOnboardingSnapshot = useCallback((nextState) => {
    setPhase(nextState.phase);
    setStep(nextState.step);
    setProfileStep(nextState.profileStep);
    setFirstName(nextState.firstName);
    setLastName(nextState.lastName);
    setEmail(nextState.email);
    setPhone(nextState.phone);
    setPhoneCc(nextState.phoneCc);
    setAgreed(nextState.agreed);
    setE164(nextState.e164);
    setPasscodeDraft(nextState.passcodeDraft);
    setPasscodeConfirm(nextState.passcodeConfirm);
    setDateOfBirth(nextState.dateOfBirth);
    setIdDoc(nextState.idDoc);
    setIdVerification(nextState.idVerification);
    setLanguages(nextState.languages);
    setPreferredLanguage(nextState.preferredLanguage);
    setBloodType(nextState.bloodType);
    setConditions(nextState.conditions);
    setAllergies(nextState.allergies);
    setMedications(nextState.medications);
  }, []);

  // ---- Auth truth (server) ----
  const [authUser, setAuthUser] = useState(null);
  const [patientRoleWriteBlocked, setPatientRoleWriteBlocked] = useState(false);
  const patientRoleWriteBlockedRef = useRef(false);
  const authEmail = authUser?.email || "";
  const authPhone = authUser?.phone || "";
  const authEmailConfirmedAt =
    authUser?.email_confirmed_at || authUser?.emailConfirmedAt || null;
  const authPhoneConfirmedAt =
    authUser?.phone_confirmed_at || authUser?.phoneConfirmedAt || null;

  const isAuthEmailVerified = !!authEmailConfirmedAt;
  const isAuthPhoneVerified = !!authPhoneConfirmedAt;
  const introSteps = React.useMemo(() => INTRO_STEPS, []);
  const afterPhoneIntroStep = ENABLE_ONBOARDING_PHONE_OTP ? "otp" : "passcodeCreate";
  const introStepIndex = React.useCallback(
    (stepName) => {
      const idx = introSteps.indexOf(stepName);
      return idx === -1 ? 0 : idx;
    },
    [introSteps]
  );
  const loggedStepKeysRef = useRef(new Set());

  // ---- Force-fresh when requested (direct URL) ----
  const didFresh = useRef(false);
  useEffect(() => {
    if (!forceFresh || didFresh.current) return;
    didFresh.current = true;
    try {
      localStorage.removeItem(LS_KEY);
    } catch {}
    const seed = buildSeedState(user?.id || authUser?.id || "");
    saveProgress(LS_KEY, seed);
    renderSourceRef.current = "resume";
    applyOnboardingSnapshot(seed);
    }, [applyOnboardingSnapshot, forceFresh, user?.id, authUser?.id]);

  useEffect(() => {
    const currentUserId = String(user?.id || authUser?.id || "").trim();
    if (!currentUserId) return;
    if (scopedRestoreUserRef.current === currentUserId) return;
    scopedRestoreUserRef.current = currentUserId;
    const stored = loadProgress(LS_KEY) ?? {};
    const ownerUserId = String(stored?.ownerUserId || "").trim();
    const nextState =
      ownerUserId === currentUserId
        ? sanitizeRestoredState(stored, currentUserId)
        : buildSeedState(currentUserId);

    if (ownerUserId !== currentUserId) {
      saveProgress(LS_KEY, nextState);
    }

    renderSourceRef.current = ownerUserId === currentUserId ? "resume" : "bootstrap";
    applyOnboardingSnapshot(nextState);
  }, [applyOnboardingSnapshot, user?.id, authUser?.id]);

    // ---- Auth bootstrap + skip logic (runs once) ----
  const didAuthBootstrap = useRef(false);
  useEffect(() => {
    if (didAuthBootstrap.current) return;
    didAuthBootstrap.current = true;

    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) return;

      const u = data?.user || null;
      if (!u) return;

      setAuthUser(u);

      if (await hasTrustedPharmacyInviteConflict(u)) {
        patientRoleWriteBlockedRef.current = true;
        setPatientRoleWriteBlocked(true);
        navigate("/onboarding/pharmacy", { replace: true });
        return;
      }

      // 🚀 THE MARATHON RESUME: Check database for existing patient profile
      const [{ data: dbProfile }, { data: ehrInfo }] = await Promise.all([
        supabase
          .from("patient_profiles")
          .select("*")
          .eq("user_id", u.id)
          .maybeSingle(),
        supabase
          .from("ehr_patient_info")
          .select("primary_language")
          .eq("patient_id", u.id)
          .maybeSingle(),
      ]);

      if (dbProfile) {
        // Sync local state from DB Truth
        if (dbProfile.full_name) {
          const parts = dbProfile.full_name.split(" ");
          setFirstName(parts[0] || "");
          setLastName(parts.slice(1).join(" ") || "");
        }
        if (dbProfile.languages) {
          const dbLanguages = normalizeStringList(dbProfile.languages);
          const dbPreferred = String(ehrInfo?.primary_language || dbLanguages[0] || "").trim();
          setPreferredLanguage(dbPreferred);
          setLanguages(orderLanguagesWithPreferred(dbLanguages, dbPreferred));
        }
        if (dbProfile.blood_type) {
          setBloodType(String(dbProfile.blood_type || "").trim());
        }
        if (dbProfile.medical_conditions) {
          setConditions(normalizeConditionList(dbProfile.medical_conditions));
        }
        if (dbProfile.allergies) {
          setAllergies(normalizeStringList(dbProfile.allergies));
        }
        if (dbProfile.current_medications) {
          setMedications(normalizeStringList(dbProfile.current_medications));
        }

      }

      const facts = collectPatientResumeFacts({
        user: u,
        dbProfile,
        profile,
        introSteps,
      });
      const stored = loadProgress(LS_KEY) ?? {};
      const previousLocal = {
        phase: String(stored?.phase || phase || "intro"),
        step: Number.isFinite(Number(stored?.step)) ? Number(stored.step) : step,
        profileStep: Number.isFinite(Number(stored?.profileStep))
          ? Number(stored.profileStep)
          : profileStep,
        ownerUserId: String(stored?.ownerUserId || "").trim(),
      };
      const resumeTarget = resolvePatientResumeTarget({
        facts,
        introSteps,
        previousPhase: previousLocal.phase,
        previousStep: previousLocal.step,
        previousProfileStep: previousLocal.profileStep,
        preserveFirstUseOrder,
      });

      setResumeFacts(facts);
      resumeDecisionRef.current = {
        stored_db_facts: facts,
        feature_flags: {
          otp_enabled: ENABLE_ONBOARDING_PHONE_OTP,
          preserve_first_use_order: preserveFirstUseOrder,
          auth_password_primary_enabled:
            String(import.meta.env.VITE_AUTH_PASSWORD_PRIMARY_ENABLED || "")
              .trim()
              .toLowerCase() === "true",
        },
        previous_local_step: previousLocal,
        computed_target_step: {
          phase: resumeTarget.phase,
          step: resumeTarget.step,
          profileStep: resumeTarget.profileStep,
          introStep: resumeTarget.introStep,
          resolved_screen_label: resumeTarget.resolvedScreenLabel,
          requires_create_password: resumeTarget.requiresCreatePassword,
        },
        skipSource: resumeTarget.skipSource,
      };

      void writeUserActivityEvent({
        userId: u.id,
        role: "patient",
        route: "/onboarding/patient",
        eventType: "patient_onboarding_resume_decision",
        metadata: {
          ...resumeDecisionRef.current,
          source: "bootstrap",
          onboarding_completed: facts.onboarding_completed,
          final_rendered_step: resumeTarget.resolvedScreenLabel,
        },
      }).catch((error) => {
        console.error("[patient-onboarding] resume decision tracking failed:", error?.message || error, {
          userId: u.id,
        });
      });

      if (
        resumeTarget.requiresCreatePassword &&
        shouldOfferCreatePassword(u)
      ) {
        renderSourceRef.current = "bootstrap";
        void writeUserActivityEvent({
          userId: u.id,
          role: "patient",
          route: "/onboarding/patient",
          eventType: "patient_onboarding_step_viewed",
          metadata: {
            phase: resumeTarget.phase,
            step: resumeTarget.step,
            profileStep: resumeTarget.profileStep,
            intro_step: resumeTarget.introStep,
            resolved_screen_label: "CreatePassword",
            reason: resumeTarget.skipSource,
            skipSource: resumeTarget.skipSource,
            source: "bootstrap",
            onboarding_completed: facts.onboarding_completed,
            has_name: facts.has_name,
            has_phone: facts.has_phone,
            has_password_marker: facts.has_password_marker,
            has_passcode_marker: facts.has_passcode_marker,
            has_dob: facts.has_dob,
            target_route: "/settings/patient/login-security/create-password",
            resume_decision: resumeDecisionRef.current,
          },
        }).catch(() => {});
        navigate("/settings/patient/login-security/create-password", {
          replace: true,
          state: {
            returnTo: "/onboarding/patient",
            firstUse: true,
            patientFirstUsePasswordWarning: true,
          },
        });
        return;
      }

      if (phase === "intro") {
        if (resumeTarget.introStep === "profileComplete" && dbProfile?.date_of_birth) {
          setDateOfBirth(String(dbProfile.date_of_birth || ""));
        }
        renderSourceRef.current = "bootstrap";
        setPhase(resumeTarget.phase);
        setStep(resumeTarget.step);
        setProfileStep(resumeTarget.profileStep);
      }

      // ── Role + Trial meta glue (set once) ─────────────────────────────
      const isTrial14 = sp.get("trial") === "14";
      try {
        const meta = u.user_metadata || {};
        const needsRole = (meta.primary_role || "").toLowerCase() !== "patient";
        const needsTrial = isTrial14 && String(meta.trial || "") !== "14";

        if (needsRole || needsTrial) {
          await supabase.auth.updateUser({
            data: {
              ...meta,
              primary_role: "patient",
              ...(needsTrial ? { trial: "14" } : {}),
            },
          });
        }
      } catch { /* ignore */ }

      // Seed contact values
      if (u.email && !email) setEmail(u.email);
      if (u.phone) {
        const parsed = splitE164ToCcLocal(u.phone);
        if (parsed?.local && !phone) setPhone(parsed.local);
        if (parsed?.cc) setPhoneCc(parsed.cc);
        if (!e164) setE164(parsed?.e164 || u.phone);
      }

    })();
  }, [phase, sp, phone, e164, introStepIndex, afterPhoneIntroStep, navigate]);

  React.useEffect(() => {
    setStep((current) => {
      const maxStep = Math.max(0, introSteps.length - 1);
      return Math.min(Math.max(current, 0), maxStep);
    });
  }, [introSteps.length]);

  // ---- Persist on any change ----
  useEffect(() => {
    saveProgress(LS_KEY, {
      phase,
      step,
      profileStep,
      ownerUserId: String(user?.id || authUser?.id || "").trim(),
      firstName,
      lastName,
      email,
      phone,
      phoneCc,
      agreed,
      e164,
      passcodeDraft,
      passcodeConfirm,
      dateOfBirth,
      idDoc,
      idVerification: normalizeIdVerificationState(idVerification),
      languages: normalizeStringList(languages),
      preferredLanguage,
      bloodType,
      conditions: normalizeConditionList(conditions),
      allergies: normalizeStringList(allergies),
      medications: normalizeStringList(medications),
    });
  }, [
    phase,
    step,
    profileStep,
    firstName,
    lastName,
    email,
    phone,
    phoneCc,
    agreed,
    e164,
    passcodeDraft,
    passcodeConfirm,
    dateOfBirth,
    idDoc,
    idVerification,
    languages,
    preferredLanguage,
    bloodType,
    conditions,
    allergies,
    medications,
    user?.id,
    authUser?.id,
  ]);

  const persistPatientOnboardingSnapshot = useCallback(
    (overrides = {}) => {
      const snapshot = {
        phase,
        step,
        profileStep,
        ownerUserId: String(user?.id || authUser?.id || "").trim(),
        firstName,
        lastName,
        email,
        phone,
        phoneCc,
        agreed,
        e164,
        passcodeDraft,
        passcodeConfirm,
        dateOfBirth,
        idDoc,
        idVerification: normalizeIdVerificationState(idVerification),
        languages: normalizeStringList(languages),
        preferredLanguage,
        bloodType,
        conditions: normalizeConditionList(conditions),
        allergies: normalizeStringList(allergies),
        medications: normalizeStringList(medications),
        ...overrides,
      };
      snapshot.idVerification = normalizeIdVerificationState(snapshot.idVerification);
      snapshot.languages = normalizeStringList(snapshot.languages);
      snapshot.preferredLanguage = String(snapshot.preferredLanguage || "").trim();
      snapshot.conditions = normalizeConditionList(snapshot.conditions);
      snapshot.allergies = normalizeStringList(snapshot.allergies);
      snapshot.medications = normalizeStringList(snapshot.medications);
      saveProgress(LS_KEY, snapshot);
      return snapshot;
    },
    [
      phase,
      step,
      profileStep,
      user?.id,
      authUser?.id,
      firstName,
      lastName,
      email,
      phone,
      phoneCc,
      agreed,
      e164,
      passcodeDraft,
      passcodeConfirm,
      dateOfBirth,
      idDoc,
      idVerification,
      languages,
      preferredLanguage,
      bloodType,
      conditions,
      allergies,
      medications,
    ]
  );

  const handlePasscodeDraftChange = useCallback(
    (nextValue) => {
      setPasscodeDraft(nextValue);
      persistPatientOnboardingSnapshot({ passcodeDraft: nextValue });
    },
    [persistPatientOnboardingSnapshot]
  );

  const handlePasscodeConfirmChange = useCallback(
    (nextValue) => {
      setPasscodeConfirm(nextValue);
      persistPatientOnboardingSnapshot({ passcodeConfirm: nextValue });
    },
    [persistPatientOnboardingSnapshot]
  );

  const handleDobChange = useCallback(
    (nextValue) => {
      setDateOfBirth(nextValue);
      persistPatientOnboardingSnapshot({ dateOfBirth: nextValue });
    },
    [persistPatientOnboardingSnapshot]
  );

  const handleIdDocChange = useCallback(
    (nextValue) => {
      setIdDoc(nextValue);
      persistPatientOnboardingSnapshot({ idDoc: nextValue });
    },
    [persistPatientOnboardingSnapshot]
  );

  const handleIdVerificationPersist = useCallback(
    (nextState) => {
      const normalized = normalizeIdVerificationState(nextState);
      setIdVerification(normalized);
      persistPatientOnboardingSnapshot({ idVerification: normalized });
    },
    [persistPatientOnboardingSnapshot]
  );

  const handleIdStepContinue = useCallback(
    async (payload = {}) => {
      const nextIdDoc = String(payload?.idDoc ?? idDoc ?? "").trim().toUpperCase();
      const nextIdVerification = normalizeIdVerificationState(
        payload?.idVerification ?? idVerification
      );
      setIdDoc(nextIdDoc);
      setIdVerification(nextIdVerification);
      persistPatientOnboardingSnapshot({
        idDoc: nextIdDoc,
        idVerification: nextIdVerification,
        profileStep: 1,
      });
      if (nextIdDoc) {
        try {
          const { data } = await supabase.auth.getUser();
          const patientUserId = String(data?.user?.id || user?.id || authUser?.id || "").trim();
          if (patientUserId) {
            await supabase
              .from("patient_profiles")
              .upsert(
                {
                  user_id: patientUserId,
                  national_id: nextIdDoc,
                  national_id_number: nextIdDoc,
                  verification_status: "pending",
                  updated_at: new Date().toISOString(),
                },
                { onConflict: "user_id" }
              );
          }
        } catch {
          // Final profile commit retries this; do not block the staging bypass lane.
        }
      }
      setProfileStep(1);
    },
    [authUser?.id, idDoc, idVerification, persistPatientOnboardingSnapshot, user?.id]
  );

  const commitPatientProfileToDb = useCallback(
    async ({ completed = false } = {}) => {
      const {
        data: { user: sessionUser },
        error: authErr,
      } = await supabase.auth.getUser();

      const patientUser = sessionUser || user || authUser;
      const patientUserId = String(patientUser?.id || "").trim();
      if (authErr || !patientUserId) {
        throw authErr || new Error("Your session is not ready yet. Please try again.");
      }

      if (patientRoleWriteBlocked || patientRoleWriteBlockedRef.current) return;

      const preview = loadPatientIntroPreview(patientUserId) || {};
      const first = textValue(firstName || preview.firstName);
      const last = textValue(lastName || preview.lastName);
      const full =
        textValue([first, last].filter(Boolean).join(" ")) ||
        textValue(preview.full_name || preview.name);
      const resolvedPhone =
        textValue(e164) ||
        textValue(preview.phone) ||
        buildE164FromParts(phoneCc, phone);
      const resolvedDob = textValue(dateOfBirth || preview.dobIso || preview.date_of_birth);
      const resolvedId = textValue(
        idDoc ||
          preview.nationalId ||
          preview.national_id ||
          preview.national_id_number ||
          preview.idPassport ||
          preview.idNumber ||
          preview.id_number
      ).toUpperCase();
      const { data: existingPatientProfile } = await supabase
        .from("patient_profiles")
        .select("phone,phone_verified_at")
        .eq("user_id", patientUserId)
        .maybeSingle();

      const patientPayload = {
        user_id: patientUserId,
        email: patientUser?.email || email || preview.email || null,
        email_verified_at: patientUser?.email_confirmed_at || null,
        updated_at: new Date().toISOString(),
      };

      if (first) patientPayload.first_name = first;
      if (last) patientPayload.last_name = last;
      if (full) patientPayload.full_name = full;
      if (resolvedPhone) {
        Object.assign(patientPayload, phoneSavePatch({ phone: resolvedPhone,
          savedPhone: existingPatientProfile?.phone, verifiedAt: existingPatientProfile?.phone_verified_at }));
      }
      if (resolvedDob) patientPayload.date_of_birth = resolvedDob;
      if (resolvedId) {
        patientPayload.national_id = resolvedId;
        patientPayload.national_id_number = resolvedId;
        patientPayload.verification_status = "pending";
      }

      const { error: patientErr } = await supabase
        .from("patient_profiles")
        .upsert(patientPayload, { onConflict: "user_id" });
      if (patientErr) throw patientErr;

      const profilePayload = {
        user_id: patientUserId,
        role: "patient",
        role_selected: true,
        onboarding_completed: completed === true,
        email: patientUser?.email || email || preview.email || null,
        updated_at: new Date().toISOString(),
      };
      if (full) profilePayload.display_name = full;

      const { error: profileErr } = await supabase
        .from("profiles")
        .upsert(profilePayload, { onConflict: "user_id" });
      if (profileErr) throw profileErr;

      try {
        if (full) await supabase.auth.updateUser({ data: { full_name: full } });
      } catch {
        // Auth metadata is secondary; patient_profiles is the source of truth.
      }
    },
    [
      authUser,
      dateOfBirth,
      e164,
      email,
      firstName,
      idDoc,
      lastName,
      patientRoleWriteBlocked,
      phone,
      phoneCc,
      user,
    ]
  );

  useEffect(() => {
    const currentUserId = String(user?.id || "").trim();
    if (!currentUserId) return;

    const introStep = introSteps[step] || introSteps[0];
    const stepKey = mapPatientOnboardingStepKey({
      phase,
      introStep,
      profileStep,
    });
    if (!stepKey || loggedStepKeysRef.current.has(stepKey)) return;
    loggedStepKeysRef.current.add(stepKey);
    const resolvedScreenLabel = resolvePatientScreenLabel({
      phase,
      introStep,
      profileStep,
    });
    const currentFacts = {
      onboarding_completed: profile?.onboarding_completed === true,
      has_name:
        !!String([firstName, lastName].filter(Boolean).join(" ") || "").trim() ||
        resumeFacts?.has_name === true,
      has_phone:
        !!String(e164 || phone || authPhone || "").trim() ||
        resumeFacts?.has_phone === true,
      has_password_marker: hasPasswordMarker(user || authUser),
      has_passcode_marker: resumeFacts?.has_passcode_marker === true,
      has_dob: !!String(dateOfBirth || "").trim() || resumeFacts?.has_dob === true,
    };
    const eventRoute = `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`;
    const source = renderSourceRef.current || "render";
    const skipSource = resumeDecisionRef.current?.skipSource || [];

    void writeOnboardingEvent({
      userId: currentUserId,
      role: "patient",
      step: stepKey,
      eventType: "step_entered",
      meta: {
        phase,
        intro_step: introStep,
        profile_step: profileStep,
      },
    }).catch((error) => {
      console.error("[patient-onboarding] step tracking failed:", error?.message || error, {
        userId: currentUserId,
        step: stepKey,
      });
    });

    void writeUserActivityEvent({
      userId: currentUserId,
      role: "patient",
      route: eventRoute,
      eventType: "patient_onboarding_step_viewed",
      metadata: {
        phase,
        step,
        profileStep,
        intro_step: introStep,
        step_key: stepKey,
        resolved_screen_label: resolvedScreenLabel,
        reason: skipSource,
        skipSource,
        source,
        onboarding_completed: currentFacts.onboarding_completed,
        has_name: currentFacts.has_name,
        has_phone: currentFacts.has_phone,
        has_password_marker: currentFacts.has_password_marker,
        has_passcode_marker: currentFacts.has_passcode_marker,
        has_dob: currentFacts.has_dob,
        final_rendered_step: resolvedScreenLabel,
        resume_decision: resumeDecisionRef.current,
      },
    }).catch((error) => {
      console.error("[patient-onboarding] screen-view tracking failed:", error?.message || error, {
        userId: currentUserId,
        step: stepKey,
      });
    });
  }, [
    authPhone,
    authUser,
    dateOfBirth,
    e164,
    firstName,
    lastName,
    location.hash,
    location.pathname,
    location.search,
    phone,
    profile?.onboarding_completed,
    resumeFacts,
    user,
    phase,
    step,
    profileStep,
    introSteps,
  ]);

// ---- Header counters (slim core onboarding; category is separate) ----
const INTRO_TOTAL = introSteps.length;
const PROFILE_TOTAL = 1;
const ALL_TOTAL = INTRO_TOTAL + PROFILE_TOTAL;

const headerStep =
  phase === "intro" ? step + 1 : INTRO_TOTAL + 1;
const headerTotal = phase === "intro" ? INTRO_TOTAL : ALL_TOTAL;
const H = { step: headerStep, total: headerTotal };

  // ---- Back helpers ----
  const backFirst = useCallback(() => {
    if (typeof backToCategory === "function") {
      backToCategory();
    } else {
      navigate("/onboarding", { replace: true });
    }
  }, [backToCategory, navigate]);

  const backIntro = () => {
    renderSourceRef.current = "back";
    setStep((s) => Math.max(0, s - 1));
  };
  const backProfile = () => {
    renderSourceRef.current = "back";
    setProfileStep((s) => Math.max(0, s - 1));
  };

/* -------------------- RENDER -------------------- */
if (phase === "intro") {
  const currentIntroStep = introSteps[step] || introSteps[0];
  const introBack = step === 0 ? backFirst : backIntro;
  const nextIntro = () => {
    renderSourceRef.current = "next_step";
    setStep((s) => Math.min(s + 1, Math.max(0, introSteps.length - 1)));
  };

  switch (currentIntroStep) {
    case "name":
      return (
        <StepName
          {...H}
          profileTable="patient_profiles"
          first={firstName}
          last={lastName}
          onChangeFirst={(next) => {
            setFirstName(next);
            persistPatientOnboardingSnapshot({ firstName: next });
          }}
          onChangeLast={(next) => {
            setLastName(next);
            persistPatientOnboardingSnapshot({ lastName: next });
          }}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "phone":
      return (
        <StepPhone
          {...H}
          value={phone}
          initialCc={phoneCc}
          agreed={agreed}
          onChangePhone={setPhone}
          onChangeCc={setPhoneCc}
          onChangeAgreed={setAgreed}
          onBack={introBack}
          onContinue={async (valE164) => {
            renderSourceRef.current = "save_success";
            setE164(valE164);
            try {
              const current = loadPatientIntroPreview(user?.id || authUser?.id || "") || {};
              savePatientIntroPreview({
                ...current,
                phone: String(valE164 || "").trim(),
                phoneVerified: false,
                phone_verified: false,
              }, user?.id || authUser?.id || "");
            } catch {}
            const normalizedPhone = String(valE164 || "").trim();
            let canonicalPatientUserId = String(user?.id || authUser?.id || "").trim();
            if (!canonicalPatientUserId) {
              try {
                const { data } = await supabase.auth.getUser();
                canonicalPatientUserId = String(data?.user?.id || "").trim();
              } catch {
                canonicalPatientUserId = "";
              }
            }
            if (canonicalPatientUserId && normalizedPhone) {
              try {
                await saveVerificationPhone({ phone: normalizedPhone, role: "patient", userId: canonicalPatientUserId });
              } catch {
                // Keep local progression even if the canonical write retries later.
              }
            }
            nextIntro();
          }}
        />
      );

      case "otp":
        if (!ENABLE_ONBOARDING_PHONE_OTP) {
          return null;
        }
        return (
          <StepOtp
          {...H}
          phone={String(e164 || authPhone || "").trim()}
          profileTable="patient_profiles"
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "passcodeCreate":
      return (
        <StepPasscodeCreate
          {...H}
          profileTable="patient_profiles"
          value={passcodeDraft}
          onChange={handlePasscodeDraftChange}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "passcodeConfirm":
      return (
        <StepPasscodeConfirm
          {...H}
          profileTable="patient_profiles"
          original={passcodeDraft}
          value={passcodeConfirm}
          onChange={handlePasscodeConfirmChange}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "dob":
      return (
        <StepDob
          {...H}
          profileTable="patient_profiles"
          value={dateOfBirth}
          onChange={handleDobChange}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "profileComplete":
      return (
        <StepProfileComplete
          {...H}
          onBack={introBack}
          onContinue={async ({ user: completedUser } = {}) => {
            await commitPatientProfileToDb({ completed: true });
            await markSettingsWelcomePending(completedUser?.id || user?.id, "patient");
            void writeUserActivityEvent({
              userId: completedUser?.id || user?.id,
              role: "patient",
              route: "/onboarding/patient",
              eventType: "patient_onboarding_settings_handoff",
              metadata: {
                source: "save_success",
                resolved_screen_label: "StepProfileComplete",
                target_route: "/settings/patient",
                onboarding_completed: true,
              },
            }).catch(() => {});
            navigate("/settings/patient", {
              replace: true,
              state: { showSettingsWelcome: true },
            });
          }}
        />
      );

    case "done":
      return (
        <StepDonePatient
          {...H}
          capturedPhone={String(e164 || "").trim()}
          onBack={introBack}
          onVerify={async () => {
            await commitPatientProfileToDb({ completed: false });
            setPhase("profile");
            setProfileStep(0);
          }}
          onSkip={async () => {
            await commitPatientProfileToDb({ completed: false });
            await markSettingsWelcomePending(user?.id, "patient");
            void writeUserActivityEvent({
              userId: user?.id,
              role: "patient",
              route: "/onboarding/patient",
              eventType: "patient_onboarding_settings_handoff",
              metadata: {
                source: "save_success",
                resolved_screen_label: "StepDonePatient",
                target_route: "/settings/patient",
                onboarding_completed: false,
              },
            }).catch(() => {});
            navigate("/settings/patient", {
              replace: true,
              state: { showSettingsWelcome: true },
            });
          }}
        />
      );

    default:
      setStep(0);
      return null;
  }
}

  if (phase === "profile") {
    if (!shouldUseDeferredOnboardingProfileSteps()) {
      return (
        <StepProfileComplete
          {...H}
          onBack={() => {
            renderSourceRef.current = "back";
            setPhase("intro");
            setStep(Math.max(0, introSteps.length - 1));
          }}
          onContinue={async ({ user: completedUser } = {}) => {
            await commitPatientProfileToDb({ completed: true });
            await markSettingsWelcomePending(completedUser?.id || user?.id, "patient");
            void writeUserActivityEvent({
              userId: completedUser?.id || user?.id,
              role: "patient",
              route: "/onboarding/patient",
              eventType: "patient_onboarding_settings_handoff",
              metadata: {
                source: "save_success",
                resolved_screen_label: "StepProfileComplete",
                target_route: "/settings/patient",
                onboarding_completed: true,
              },
            }).catch(() => {});
            navigate("/settings/patient", {
              replace: true,
              state: { showSettingsWelcome: true },
            });
          }}
        />
      );
    }

    switch (profileStep) {
      case 0:
      return (
        <StepIDPassport
          {...H}
          profileTable="patient_profiles"
          value={idDoc}
          onChange={handleIdDocChange}
          persistedState={idVerification}
          onPersistState={handleIdVerificationPersist}
          onBack={() => {
            renderSourceRef.current = "back";
            setPhase("intro");
            setStep(Math.max(0, introSteps.length - 1));
          }}
          onContinue={handleIdStepContinue}
        />
      );

    case 1: // In Phase: Profile
      return (
        <StepLanguages
          {...H}
          profileTable="patient_profiles"
          selected={languages}
          preferred={preferredLanguage || normalizeStringList(languages)[0] || ""}
          onPreferredChange={(nextPreferred) => {
            const preferred = String(nextPreferred || "").trim();
            setPreferredLanguage(preferred);
            const ordered = [
              preferred,
              ...normalizeStringList(languages).filter(
                (item) => item.toLowerCase() !== preferred.toLowerCase()
              ),
            ].filter(Boolean);
            setLanguages(ordered);
            persistPatientOnboardingSnapshot({
              preferredLanguage: preferred,
              languages: ordered,
            });
          }}
          onChange={(nextValue) => {
            const normalized = normalizeStringList(nextValue);
            setLanguages(normalized);
            persistPatientOnboardingSnapshot({
              languages: normalized,
              preferredLanguage,
            });
          }}
          onBack={() => setProfileStep(0)}
          onContinue={() => {
            renderSourceRef.current = "next_step";
            persistPatientOnboardingSnapshot({
              languages: normalizeStringList(languages),
              preferredLanguage: preferredLanguage || normalizeStringList(languages)[0] || "",
              profileStep: 2,
            });
            setProfileStep(2);
          }}
        />
      );

      case 2:
        return (
          <StepBloodType
            {...H}
            value={bloodType}
            onChange={(nextValue) => {
              const normalized = String(nextValue || "").trim();
              setBloodType(normalized);
              persistPatientOnboardingSnapshot({ bloodType: normalized });
            }}
            onBack={backProfile}
          onContinue={() => setProfileStep(3)}
          />
        );
      case 3:
        return (
          <StepConditions
            {...H}
            selected={conditions}
            onChange={(nextValue) => {
              const normalized = normalizeConditionList(nextValue);
              setConditions(normalized);
              persistPatientOnboardingSnapshot({ conditions: normalized });
            }}
            onBack={backProfile}
            onContinue={(nextValue) => {
              renderSourceRef.current = "next_step";
              const normalized = normalizeConditionList(nextValue);
              setConditions(normalized);
              persistPatientOnboardingSnapshot({
                conditions: normalized,
                profileStep: 4,
              });
              setProfileStep(4);
            }}
          />
        );
      case 4:
        return (
          <StepAllergies
            {...H}
            selected={allergies}
            onChange={(nextValue) => {
              const normalized = normalizeStringList(nextValue);
              setAllergies(normalized);
              persistPatientOnboardingSnapshot({ allergies: normalized });
            }}
            onBack={backProfile}
            onContinue={() => setProfileStep(5)}
          />
        );
      case 5:
        return (
          <StepMedications
            {...H}
            selected={medications}
            onChange={(nextValue) => {
              const normalized = normalizeStringList(nextValue);
              setMedications(normalized);
              persistPatientOnboardingSnapshot({ medications: normalized });
            }}
            onBack={backProfile}
            onContinue={() => setProfileStep(6)}
          />
        );
      case 6:
        return (
          <StepProfileComplete
            {...H}
            onBack={backProfile}
          onContinue={async ({ user: completedUser } = {}) => {
            await commitPatientProfileToDb({ completed: true });
            await markSettingsWelcomePending(completedUser?.id || user?.id, "patient");
            void writeUserActivityEvent({
              userId: completedUser?.id || user?.id,
              role: "patient",
              route: "/onboarding/patient",
              eventType: "patient_onboarding_settings_handoff",
              metadata: {
                source: "save_success",
                resolved_screen_label: "StepProfileComplete",
                target_route: "/settings/patient",
                onboarding_completed: true,
              },
            }).catch(() => {});
            navigate("/settings/patient", {
              replace: true,
              state: { showSettingsWelcome: true },
              });
            }}
          />
        );
      default:
        return null;
    }
  }

  return null;
}
